import { Button, Checkbox } from '@mantine/core';
import { useTranslation } from '../../context/TranslationContext';
import AddressSearch from '../AddressSearch';

export default function AddressSection({
  type = 'shipping',
  addresses, selectedId, onSelectId,
  showForm, setShowAddressForm,
  form, onFormChange, onSelectResult,
  saving, error, onSubmit,
  sameAsShipping, onSameAsShippingChange, selectedShippingAddress,
  deliveryUnavailableIds,
}) {
  const { t } = useTranslation();
  const isShipping = type === 'shipping';
  const radioName = isShipping ? 'address' : 'billingAddress';
  const showSameAsShipping = !isShipping && showForm && onSameAsShippingChange && selectedShippingAddress;

  const formatAddress = (addr) => (
    <>
      <strong>{addr.full_name}</strong>
      {!isShipping && addr.company_name ? ` (${addr.company_name})` : ''}, {addr.address_line1}
      {addr.address_line2 ? `, ${addr.address_line2}` : ''}, {addr.postal_code} {addr.city}, {addr.country}
      {!isShipping && addr.vat_number ? <span> (VAT: {addr.vat_number})</span> : null}
      {addr.is_default ? <span className="badge">{t('defaultAddress', 'Default')}</span> : null}
    </>
  );

  const emptyText = isShipping
    ? t('noAddress', 'No delivery address added yet.')
    : t('noBillingAddress', 'No billing address added yet.');

  const addLabel = isShipping
    ? t('addAddress', 'Add new address')
    : t('addAddress', 'Add new billing address');

  return (
    <section
      className="address-section"
      aria-labelledby={`address-section-${type}-title`}
    >
      <h3 id={`address-section-${type}-title`}>
        {isShipping ? t('deliveringTo', 'Delivering to') : (
          <>
            {t('billingAddress', 'Billing Address')} <span aria-hidden="true">*</span>
          </>
        )}
      </h3>

      <fieldset
        style={{ border: 0, padding: 0, margin: 0 }}
        aria-label={isShipping ? t('savedDeliveryAddresses', 'Saved delivery addresses') : t('savedBillingAddresses', 'Saved billing addresses')}
      >
        <legend className="sr-only">{isShipping ? t('savedDeliveryAddresses', 'Saved delivery addresses') : t('savedBillingAddresses', 'Saved billing addresses')}</legend>
        {addresses.length > 0 ? (
          <div className="address-list" role="radiogroup" style={{ marginTop: '10px' }}>
            {addresses.map(addr => {
              const radioId = `address-${type}-${addr.id}`;
              return (
                <label key={addr.id} htmlFor={radioId} className={`address-radio${selectedId === addr.id ? ' selected' : ''}`}>
                  <input
                    id={radioId}
                    type="radio"
                    name={radioName}
                    value={addr.id}
                    checked={selectedId === addr.id}
                    onChange={() => onSelectId(addr.id)}
                  />
                  <span>{formatAddress(addr)}</span>
                  {isShipping && deliveryUnavailableIds?.[addr.id] ? (
                    <span
                      className="delivery-unavailable-badge"
                      role="note"
                      style={{ display: 'inline-block', marginLeft: 8, padding: '1px 8px', borderRadius: 4, fontSize: '0.8em', fontWeight: 600, color: '#9a3412', background: '#ffedd5', border: '1px solid #fdba74' }}
                    >
                      {t('deliveryNotAvailable', 'Delivery not available')}
                    </span>
                  ) : null}
                </label>
              );
            })}
          </div>
        ) : (
          <div role="status" style={{ color: '#666', marginBottom: 8 }}>{emptyText}</div>
        )}
      </fieldset>

      {!showForm && (
        <Button variant="light" color="blue" onClick={() => setShowAddressForm(true)}>
          {addLabel}
        </Button>
      )}

      {showForm && (
        <form
          className="address-form"
          onSubmit={onSubmit}
          aria-label={isShipping ? t('newDeliveryAddressForm', 'New delivery address') : t('newBillingAddressForm', 'New billing address')}
          noValidate
        >
          {showSameAsShipping && (
            <fieldset style={{ border: 0, padding: 0, margin: '0 0 8px 0' }}>
              <legend className="sr-only">{t('sameAsShippingAddress', 'Same as shipping address')}</legend>
              <Checkbox
                checked={sameAsShipping || false}
                onChange={e => onSameAsShippingChange(e.currentTarget.checked)}
                label={t('sameAsShippingAddress', 'Same as shipping address')}
              />
            </fieldset>
          )}
          <AddressSearch onSelectResult={onSelectResult} showDeliveryDisclaimer={isShipping} />
          <input
            id={`address-form-${type}-full_name`}
            name="full_name"
            placeholder={t('fullName', 'Full name')}
            value={form.full_name}
            onChange={onFormChange}
            aria-label={t('fullName', 'Full name')}
            required
          />
          {!isShipping && (
            <>
              <input
                id={`address-form-${type}-company_name`}
                name="company_name"
                placeholder={t('companyName', 'Company name (optional)')}
                value={form.company_name || ''}
                onChange={onFormChange}
                aria-label={t('companyName', 'Company name (optional)')}
              />
              <input
                id={`address-form-${type}-vat_number`}
                name="vat_number"
                placeholder={t('vatNumber', 'VAT number (optional)')}
                value={form.vat_number || ''}
                onChange={onFormChange}
                aria-label={t('vatNumber', 'VAT number (optional)')}
              />
            </>
          )}
          <input
            id={`address-form-${type}-address_line1`}
            name="address_line1"
            placeholder={t('addressLine1', 'Address line 1')}
            value={form.address_line1}
            onChange={onFormChange}
            aria-label={t('addressLine1', 'Address line 1')}
            required
          />
          <input
            id={`address-form-${type}-address_line2`}
            name="address_line2"
            placeholder={t('addressLine2', 'Address line 2')}
            value={form.address_line2}
            onChange={onFormChange}
            aria-label={t('addressLine2', 'Address line 2')}
          />
          <input
            id={`address-form-${type}-city`}
            name="city"
            placeholder={t('city', 'City')}
            value={form.city}
            onChange={onFormChange}
            aria-label={t('city', 'City')}
            required
          />
          <input
            id={`address-form-${type}-postal_code`}
            name="postal_code"
            placeholder={t('postalCode', 'Postal code')}
            value={form.postal_code}
            onChange={onFormChange}
            aria-label={t('postalCode', 'Postal code')}
            required
          />
          <input
            id={`address-form-${type}-country`}
            name="country"
            placeholder={t('country', 'Country')}
            value={form.country}
            onChange={onFormChange}
            aria-label={t('country', 'Country')}
            required
          />
          <Button type="submit" loading={saving}>
            {saving ? t('saving', 'Saving...') : isShipping ? t('saveAddress', 'Save Address') : t('saveAddress', 'Save Billing Address')}
          </Button>
          <Button variant="subtle" color="gray" type="button" onClick={() => {
            setShowAddressForm(false);
            if (sameAsShipping && onSameAsShippingChange) onSameAsShippingChange(false);
          }}>
            {t('cancel', 'Cancel')}
          </Button>
          {error ? (
            <div
              id={`address-form-${type}-error`}
              role="alert"
              style={{ color: 'red', marginTop: 8 }}
            >
              {error}
            </div>
          ) : null}
        </form>
      )}
    </section>
  );
}
